import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { RequestApiArchiveCacheService } from '../request-api/request-api-archive-cache.service';
import { UploadHistoryService } from '../services/upload-history.service';
import { formatBytes } from '../storage-usage/format-bytes';

/** How the header icon and progress bar surface the current fill level. */
export type StorageUsageLevel = 'normal' | 'warning' | 'critical';

/** The `usedBytes / limitBytes` ratio at which the level turns yellow / red. */
export const WARNING_THRESHOLD = 0.7;
export const CRITICAL_THRESHOLD = 0.9;

/** Default per-user cache limit: 500 MB. */
export const DEFAULT_LIMIT_BYTES = 500 * 1024 * 1024;

// localStorage (not IndexedDB) so the limit can be read synchronously at store
// construction; naming follows the existing `ed_`-prefixed convention.
const LIMIT_STORAGE_KEY = 'ed_storage_limit_bytes';

/**
 * A single evictable cached archive, normalized across the two backing caches
 * so eviction can reason about them uniformly.
 */
export interface EvictableEntry {
  cache: 'upload' | 'request-api';
  key: string;
  name: string;
  size: number;
  /** Epoch ms of the last read (falls back to creation time until touched). */
  lastAccess: number;
  /** Only set for `request-api` entries — needed to address the cache remove. */
  connectionId?: string;
  packageIri?: string;
}

interface StorageQuotaState {
  limitBytes: number;
  usedBytes: number;
  entries: EvictableEntry[];
  busy: boolean;
}

function readStorageLimit(): number {
  const raw = localStorage.getItem(LIMIT_STORAGE_KEY);
  const parsed = raw === null ? NaN : Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_LIMIT_BYTES;
}

/**
 * Root-provided store that owns the managed-cache storage quota: it aggregates
 * the uploaded- and request-api archive caches, exposes usage vs. a persisted
 * limit, evicts least-recently-used archives when the limit is exceeded, and
 * reconciles orphaned OPFS files against IndexedDB metadata.
 *
 * "Used" is the sum of tracked archive sizes (managed-cache measurement) — it
 * intentionally excludes IPFS/Helia blocks and search indexes, so the number is
 * fully enforceable by eviction.
 */
export const storageQuotaStore = signalStore(
  { providedIn: 'root' },
  withState<StorageQuotaState>(() => ({
    limitBytes: readStorageLimit(),
    usedBytes: 0,
    entries: [],
    busy: false,
  })),
  withComputed((store) => {
    const usageRatio = computed(() => {
      const limit = store.limitBytes();
      return limit > 0 ? store.usedBytes() / limit : 0;
    });
    return {
      usageRatio,
      usagePercent: computed(() => Math.min(100, Math.round(usageRatio() * 100))),
      level: computed<StorageUsageLevel>(() => {
        const ratio = usageRatio();
        if (ratio >= CRITICAL_THRESHOLD) {
          return 'critical';
        }
        if (ratio >= WARNING_THRESHOLD) {
          return 'warning';
        }
        return 'normal';
      }),
      usedFormatted: computed(() => formatBytes(store.usedBytes())),
      limitFormatted: computed(() => formatBytes(store.limitBytes())),
    };
  }),
  withMethods(
    (
      store,
      uploads = inject(UploadHistoryService),
      reqApi = inject(RequestApiArchiveCacheService),
    ) => {
      async function collectEntries(): Promise<EvictableEntry[]> {
        const [uploadEntries, reqEntries] = await Promise.all([
          uploads.listEntries(),
          reqApi.list(),
        ]);
        return [
          ...uploadEntries.map<EvictableEntry>((entry) => ({
            cache: 'upload',
            key: entry.key,
            name: entry.name,
            size: entry.size ?? 0,
            lastAccess: entry.lastAccess ?? entry.date?.getTime() ?? 0,
          })),
          ...reqEntries.map<EvictableEntry>((entry) => ({
            cache: 'request-api',
            key: entry.key,
            name: entry.name ?? entry.packageIri,
            size: entry.size ?? 0,
            lastAccess: entry.lastAccess ?? entry.date ?? 0,
            connectionId: entry.connectionId,
            packageIri: entry.packageIri,
          })),
        ];
      }

      async function removeEntry(entry: EvictableEntry): Promise<void> {
        if (entry.cache === 'upload') {
          await uploads.removeByKey(entry.key);
        } else if (entry.connectionId && entry.packageIri) {
          await reqApi.remove(entry.connectionId, entry.packageIri);
        }
      }

      async function refresh(): Promise<void> {
        const entries = await collectEntries();
        const usedBytes = entries.reduce((sum, entry) => sum + entry.size, 0);
        patchState(store, { entries, usedBytes });
      }

      async function enforceLimit(): Promise<void> {
        patchState(store, { busy: true });
        try {
          await refresh();
          const limit = store.limitBytes();
          // Oldest-accessed first, so eviction is least-recently-used.
          const queue = [...store.entries()].sort(
            (a, b) => a.lastAccess - b.lastAccess,
          );
          let used = store.usedBytes();
          for (const victim of queue) {
            if (used <= limit) {
              break;
            }
            await removeEntry(victim);
            used -= victim.size;
          }
          await refresh();
        } finally {
          patchState(store, { busy: false });
        }
      }

      async function setLimit(bytes: number): Promise<void> {
        const limitBytes =
          Number.isFinite(bytes) && bytes > 0
            ? Math.floor(bytes)
            : DEFAULT_LIMIT_BYTES;
        localStorage.setItem(LIMIT_STORAGE_KEY, String(limitBytes));
        patchState(store, { limitBytes });
        await enforceLimit();
      }

      async function clearArchives(): Promise<void> {
        patchState(store, { busy: true });
        try {
          await uploads.clear();
          const reqEntries = await reqApi.list();
          for (const entry of reqEntries) {
            await reqApi.remove(entry.connectionId, entry.packageIri);
          }
          await refresh();
        } finally {
          patchState(store, { busy: false });
        }
      }

      /**
       * OPFS cleanup: reconcile OPFS root files against IndexedDB metadata.
       * Removes root files with no metadata entry, and metadata whose OPFS file
       * is gone. Best-effort — never throws (runs on startup). Subdirectory
       * caches (flexsearch index, fs-cache) are skipped: only root-level files
       * belong to the two archive caches.
       */
      async function cleanupOrphans(): Promise<void> {
        try {
          const root = await navigator.storage.getDirectory();
          const [uploadEntries, reqEntries] = await Promise.all([
            uploads.listEntries(),
            reqApi.list(),
          ]);
          const validKeys = new Set<string>([
            ...uploadEntries.map((entry) => entry.key),
            ...reqEntries.map((entry) => entry.key),
          ]);

          // Remove orphan OPFS root files (present on disk, no metadata).
          for await (const [name, handle] of (
            root as unknown as {
              entries(): AsyncIterableIterator<
                [string, FileSystemHandle]
              >;
            }
          ).entries()) {
            if (handle.kind !== 'file' || validKeys.has(name)) {
              continue;
            }
            try {
              await root.removeEntry(name);
            } catch (error) {
              console.warn('Failed to remove orphan OPFS file', name, error);
            }
          }

          // Remove dangling metadata (entry present, OPFS file gone).
          for (const entry of uploadEntries) {
            if (!(await opfsFileExists(root, entry.key))) {
              await uploads.removeByKey(entry.key);
            }
          }
          for (const entry of reqEntries) {
            if (!(await opfsFileExists(root, entry.key))) {
              await reqApi.remove(entry.connectionId, entry.packageIri);
            }
          }

          await refresh();
        } catch (error) {
          console.warn('OPFS cleanup failed', error);
        }
      }

      return { refresh, enforceLimit, setLimit, clearArchives, cleanupOrphans };
    },
  ),
  withHooks({
    onInit(store) {
      void store.refresh();
      void store.cleanupOrphans();
    },
  }),
);

async function opfsFileExists(
  root: FileSystemDirectoryHandle,
  key: string,
): Promise<boolean> {
  try {
    await root.getFileHandle(key);
    return true;
  } catch {
    return false;
  }
}
